import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquarePlus, Bug, Lightbulb, MessageCircle, X, Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { betaAPI } from '../services/api';

const BetaFeedbackButton = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState(null); // 'bug', 'feature', 'general'
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const feedbackTypes = [
    { id: 'bug', label: 'Report Bug', icon: Bug, color: 'text-red-400 bg-red-500/20 border-red-500/30 hover:bg-red-500/30' },
    { id: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30 hover:bg-yellow-500/30' },
    { id: 'general', label: 'General Feedback', icon: MessageCircle, color: 'text-blue-400 bg-blue-500/20 border-blue-500/30 hover:bg-blue-500/30' },
  ];

  const severityOptions = [
    { id: 'low', label: 'Low', color: 'bg-blue-500' },
    { id: 'medium', label: 'Medium', color: 'bg-yellow-500' },
    { id: 'high', label: 'High', color: 'bg-orange-500' },
    { id: 'critical', label: 'Critical', color: 'bg-red-500' },
  ];

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim() || !type) return;

    setSubmitting(true);
    try {
      await betaAPI.submitFeedback(
        type,
        title.trim(),
        description.trim(),
        location.pathname,
        type === 'bug' ? severity : 'medium'
      );
      
      // Track the feedback submission
      betaAPI.track('beta_feedback', 'submit', { type, page: location.pathname });
      
      setSubmitted(true);
      setTimeout(() => {
        resetForm();
      }, 2000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setType(null);
    setTitle('');
    setDescription('');
    setSeverity('medium');
    setSubmitted(false);
    setIsOpen(false);
  };

  // Success state
  if (submitted) {
    return (
      <div className="fixed bottom-24 sm:bottom-20 right-6 z-50">
        <div className="bg-[#0B0F1A] border border-green-500/30 rounded-2xl p-6 shadow-2xl w-80">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
            <p className="text-gray-400 text-sm">Your feedback helps us improve STAFFED.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-24 sm:bottom-20 right-6 z-50">
      {/* Feedback Panel */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 sm:w-96 bg-[#0B0F1A] border border-white/10 rounded-2xl shadow-2xl overflow-hidden mb-2">
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
            <div className="flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-[#D4AF37]" />
              <span className="font-semibold text-white">Beta Feedback</span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4">
            {/* Type Selection */}
            {!type ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-400 mb-4">What would you like to share?</p>
                {feedbackTypes.map((ft) => (
                  <button
                    key={ft.id}
                    onClick={() => setType(ft.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${ft.color}`}
                  >
                    <ft.icon className="w-5 h-5" />
                    <span className="font-medium text-white">{ft.label}</span>
                  </button>
                ))}
              </div>
            ) : (
              /* Feedback Form */
              <div className="space-y-4">
                {/* Back button */}
                <button
                  onClick={() => setType(null)}
                  className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
                >
                  <span>←</span> Back
                </button>

                {/* Type badge */}
                <div className="flex items-center gap-2">
                  {feedbackTypes.find(ft => ft.id === type)?.icon && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                      type === 'bug' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                      type === 'feature' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                      'bg-blue-500/20 text-blue-400 border-blue-500/30'
                    }`}>
                      {feedbackTypes.find(ft => ft.id === type)?.label}
                    </span>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Title</label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={
                      type === 'bug' ? "What's broken?" :
                      type === 'feature' ? "Feature name" :
                      "Feedback title"
                    }
                    className="bg-white/5 border-white/20 text-white rounded-xl"
                  />
                </div>

                {/* Severity (for bugs only) */}
                {type === 'bug' && (
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Severity</label>
                    <div className="flex gap-2">
                      {severityOptions.map((sev) => (
                        <button
                          key={sev.id}
                          onClick={() => setSeverity(sev.id)}
                          className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                            severity === sev.id
                              ? `${sev.color} text-white`
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {sev.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Description</label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={
                      type === 'bug' ? "What happened? What did you expect?" :
                      type === 'feature' ? "Describe the feature and why it would help..." :
                      "Share your thoughts..."
                    }
                    className="bg-white/5 border-white/20 text-white rounded-xl min-h-[100px] resize-none"
                  />
                </div>

                {/* Current page indicator */}
                <p className="text-xs text-gray-500">
                  Page: {location.pathname}
                </p>

                {/* Submit button */}
                <Button
                  onClick={handleSubmit}
                  disabled={!title.trim() || !description.trim() || submitting}
                  className="w-full rounded-xl bg-[#D4AF37] text-[#0B0F1A] hover:bg-[#E5C76B] font-semibold"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Feedback
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`group flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all duration-300 ${
          isOpen 
            ? 'bg-white/10 border border-white/20' 
            : 'bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] hover:shadow-[0_0_20px_rgba(188,144,40,0.3)]'
        }`}
        data-testid="beta-feedback-btn"
      >
        {isOpen ? (
          <X className="w-5 h-5 text-white" />
        ) : (
          <>
            <MessageSquarePlus className="w-5 h-5 text-[#0B0F1A]" />
            <span className="text-[#0B0F1A] font-semibold text-sm hidden sm:inline">Beta Feedback</span>
          </>
        )}
      </button>
    </div>
  );
};

export default BetaFeedbackButton;
